import { useState } from "react";
import { GstRestS } from "../../rest/gst-rest-service";
import { GstAnalysisStatus } from "../../interfaces/interfaces";
import { useGstReconSocket, GstJobUpdate } from "./useGstReconSocket";

/**
 * Shared hook for tracking GST job status across Underwriting + Monthly Report.
 *
 * Replaces the old /gstanalysis polling path with:
 *   1. Initial load via GET /gst/jobs/status (active jobs only)
 *   2. Realtime updates via useGstReconSocket (GST_RECON_JOB_UPDATE events)
 *
 * Maps into the same `gstAnalysisJobs` state shape ({ status, errorDetails })
 * keyed by jobId (= statementJobId in form data = gst_recon_job.id), so
 * GstAccount.tsx's lookup `gstAnalysisJobs[statementJobId]` works unchanged.
 */
export function useGstJobTracking() {
  const [gstAnalysisJobs, setGstAnalysisJobs] = useState<{ [key: string]: any }>({});

  // ── Initial load (page load / reconnect) ──────────────────────────────
  // Fetches active jobs only — completed jobs are delivered by the socket
  // the moment they finish. If the page was reloaded AFTER a job completed,
  // we won't see it here, but GstAccount.tsx also marks isVerified=true
  // via the form data (isVerified flag), so "Connected" still shows.
  const fetchGstAnalysisJobs = async () => {
    try {
      const res = await GstRestS.getJobStatus();
      if (res?.success) {
        const jobs = res.data?.jobs || [];
        const jobsObj = jobs.reduce((acc: any, job: any) => {
          acc[String(job.jobId)] = {
            status:
              job.status === "running"
                ? GstAnalysisStatus.inProgress
                : job.status === "completed"
                ? GstAnalysisStatus.success
                : job.status === "failed"
                ? GstAnalysisStatus.failure
                : GstAnalysisStatus.inProgress,
            errorDetails: null,
            jobType: job.jobType,
            gstin: job.gstin,
          };
          return acc;
        }, {});
        setGstAnalysisJobs(jobsObj);
      }
    } catch (error) {
      console.log("error fetching gst job status", error);
    }
  };

  // ── Realtime updates ──────────────────────────────────────────────────
  // Socket fires when a gst_recon_job changes status. We map it into the
  // SAME gstAnalysisJobs state, keyed by jobId (= statementJobId in form data).
  useGstReconSocket((update: GstJobUpdate) => {
    setGstAnalysisJobs((prev) => ({
      ...prev,
      [String(update.jobId)]: {
        status:
          update.status === "completed"
            ? GstAnalysisStatus.success
            : update.status === "failed"
            ? GstAnalysisStatus.failure
            : GstAnalysisStatus.inProgress,
        errorDetails: update.error ? { errorMessage: update.error.message } : null,
        jobType: update.jobType,
        gstin: update.gstin,
      },
    }));
  });

  return { gstAnalysisJobs, setGstAnalysisJobs, fetchGstAnalysisJobs };
}